/*
 * ============================================================
 * CCS UPLOADS — assets/ccs-upload.js
 * ============================================================
 * Puts a file in the sib-design bucket and returns its URL.
 *
 * Two steps, both required:
 *   1. ask SIBServices for a presigned POST  (public/getPresignedPostData)
 *   2. POST the file straight to S3 with the fields it hands back
 *
 * The browser never holds an AWS credential -- the presign is scoped to one
 * key, one content type, a size range and 15 minutes.
 *
 * Everything lands in the same bucket the SIB design builder uses, which is
 * also the only bucket the CCS API will accept a URL from: lib/assets.js
 * rejects anything else, because these URLs become product images and print
 * files. So an upload that does not go through here cannot be saved to a
 * listing, by design.
 */
(function (window) {
  'use strict';

  // Same endpoint the SIB builder uses. Overridable so a dev store can point
  // at a different stack without editing the theme.
  var PRESIGN_URL = (window.CCS_PRESIGN_URL ||
    'https://dix7ak1o8b.execute-api.us-east-1.amazonaws.com/dev/public/getPresignedPostData');

  var MAX_BYTES = 10 * 1024 * 1024;   // matches the presign's content-length-range
  var MIN_BYTES = 100;

  function presign(filename) {
    return fetch(PRESIGN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: filename })
    }).then(function (response) {
      if (!response.ok) throw new Error('Upload could not be started.');
      return response.json();
    }).then(function (payload) {
      if (!payload || payload.error || !payload.data) {
        throw new Error((payload && payload.message) || 'Upload could not be started.');
      }
      return payload.data; // { url, fields }
    });
  }

  /*
   * S3 answers a presigned POST with 204 and an empty body, so success is the
   * status code -- there is nothing to parse. The object URL is built from the
   * key that was signed rather than read from a response header, because the
   * key is the thing we asked for and already know.
   */
  function putToS3(data, file) {
    var form = new FormData();
    Object.keys(data.fields).forEach(function (field) {
      form.append(field, data.fields[field]);
    });
    // File must be appended last: S3 ignores any field after it.
    form.append('file', file);

    return fetch(data.url, { method: 'POST', body: form }).then(function (response) {
      if (!response.ok) throw new Error('That file could not be uploaded.');
      return 'https://sib-design.s3.amazonaws.com/' + data.fields.key;
    });
  }

  function upload(file, options) {
    var opts = options || {};
    if (!file) return Promise.reject(new Error('No file selected.'));
    if (file.size > MAX_BYTES) {
      return Promise.reject(new Error('That file is too large. The limit is 10MB.'));
    }
    if (file.size < MIN_BYTES) {
      return Promise.reject(new Error('That file looks empty.'));
    }
    if (opts.accept && opts.accept.indexOf(file.type) === -1) {
      return Promise.reject(new Error('That file type is not supported.'));
    }

    // The presign keys on the filename's extension for content type, so a name
    // is required even when the browser gives a blob without one.
    var name = file.name || ('upload-' + Date.now() + '.png');

    return presign(name).then(function (data) { return putToS3(data, file); });
  }

  window.CCSUpload = {
    upload: upload,
    image: function (file) {
      return upload(file, { accept: ['image/png', 'image/jpeg', 'image/webp'] });
    }
  };
})(window);
